/**
 * F5: соответствие задач стандартам — MITRE ATT&CK + нормативная база РК.
 *
 * MITRE ATT&CK: тактики/техники атакующего (T...) и митигации (M...) —
 * для Blue Team задач — митигации. КР: нормативные ссылки, актуальные
 * для прокуратуры/СЦК/полиции (УК, УПК, законы об информации и ПД).
 *
 * Это «слой смысла» поверх контента: не хранится в tasks_json,
 * подмешивается в taskDtos() — контент сценариев не мигрируется.
 */

export interface TaskMapping {
  /** MITRE ATT&CK: техники (T...) и/или митигации (M...). */
  mitre?: string[];
  /** Нормативные ссылки РК (ключи из KZ_REFS). */
  kz?: string[];
}

/** Названия техник MITRE ATT&CK (кратко, RU). */
export const MITRE_NAMES: Record<string, string> = {
  "T1003.008": "Креденшел-дампинг: /etc/passwd и /etc/shadow",
  "T1005": "Сбор данных с локальной системы (логи/файлы)",
  "T1018": "Разведка: конфигурация сети системы",
  "T1021.004": "Удалённые сервисы: SSH",
  "T1046": "Разведка: службы сети (порты/баннеры)",
  "T1059.004": "Выполнение: Unix Shell (инъекция команд)",
  "T1078": "Учётные записи: легитимные аккаунты",
  "T1087": "Разведка: учётные записи (перечисление)",
  "T1110.001": "Брут-форс: подбор паролей",
  "T1190": "Эксплойт публичного приложения (внешняя уязвимость)",
  "T1528": "Кража токена доступа приложения (IDOR/сессии)",
  "M1000": "Митигация: обучение пользователей",
  "M1023": "Митигация: многофакторная аутентификация",
};

/** Нормативные ссылки РК (ключ → полное название). */
export const KZ_REFS: Record<string, string> = {
  KZ_Criminal204:
    "УК РК, ст. 204 — незаконные действия с информацией в компьютерных системах",
  KZ_InformationLaw:
    "Закон РК «О информации и информационной защите» (2021) — меры защиты информации",
  KZ_PersonalData:
    "Закон РК «О персональных данных и их защите» (2021) — утечка ПД",
  KZ_CriminalProcedure:
    "УПК РК — порядок изъятия и проверки цифровых доказательств (цепочка сохранности)",
};

/** Короткие метки для чипов в карточке задачи (tooltip — полное название). */
export const KZ_SHORT: Record<string, string> = {
  KZ_Criminal204: "УК РК ст. 204",
  KZ_InformationLaw: "Закон «О информации» (2021)",
  KZ_PersonalData: "Закон «О ПД» (2021)",
  KZ_CriminalProcedure: "УПК РК — доказательства",
};

/**
 * Соответствие: scenarioId → taskId → mapping.
 * Задачи без записи — без соответствия (например, чистая разведка t1–t3).
 */
export const TASK_MAPPING: Record<string, Record<string, TaskMapping>> = {
  // ── Жеке Kali ортасы (основная практика) ─────────────────────────────
  "personal-kali": {
    t1: { mitre: ["T1046"], kz: [] },
    t2: { mitre: ["T1190", "T1528"], kz: ["KZ_Criminal204"] },
    t3: { mitre: ["T1046", "T1018"], kz: [] },
    t4: { mitre: ["T1087"], kz: ["KZ_PersonalData"] },
    t5: {
      mitre: ["T1110.001", "T1190", "T1059.004", "T1005"],
      kz: ["KZ_Criminal204"],
    },
    t6: { mitre: ["T1018"], kz: [] },
    t7: { mitre: ["T1110.001", "T1021.004"], kz: ["KZ_Criminal204"] },
    t8: { mitre: ["T1190", "T1005"], kz: ["KZ_CriminalProcedure"] },
    t9: { mitre: ["M1023", "M1000"], kz: ["KZ_InformationLaw"] },
    t10: { mitre: [], kz: ["KZ_CriminalProcedure", "KZ_InformationLaw"] },
  },

  // ── Web-инцидент: скомпрометированный сервер ────────────────────────
  "lab-scenario-1": {
    t1: { mitre: ["T1046"], kz: [] },
    t2: { mitre: ["T1018"], kz: [] },
    t3: { mitre: ["T1046"], kz: [] },
    t4: { mitre: ["T1046"], kz: [] },
    t5: { mitre: ["T1087"], kz: ["KZ_PersonalData"] },
    t6: { mitre: ["T1190", "T1528"], kz: ["KZ_Criminal204", "KZ_PersonalData"] },
    t7: { mitre: ["T1190", "T1528"], kz: ["KZ_PersonalData"] },
    t8: { mitre: ["T1110.001", "T1021.004"], kz: ["KZ_Criminal204"] },
    t9: { mitre: ["M1023", "M1000"], kz: ["KZ_InformationLaw"] },
    t10: { mitre: [], kz: ["KZ_CriminalProcedure", "KZ_InformationLaw"] },
  },

  // ── Разведка в изолированной сети ───────────────────────────────────
  "lab-scenario-2": {
    t1: { mitre: ["T1018"], kz: [] },
    t2: { mitre: ["T1018"], kz: [] },
    t3: { mitre: ["T1018"], kz: [] },
    t4: { mitre: ["T1046"], kz: [] },
    t5: { mitre: ["T1046"], kz: [] },
    t6: { mitre: ["T1046"], kz: [] },
    t7: { mitre: ["T1046"], kz: [] },
    t8: { mitre: ["T1046"], kz: [] },
    t9: { mitre: ["T1046"], kz: ["KZ_InformationLaw"] },
    t10: { mitre: ["T1046", "T1018"], kz: ["KZ_InformationLaw"] },
  },

  // ── Политики и защита: Blue Team ────────────────────────────────────
  "lab-scenario-3": {
    t1: { mitre: [], kz: ["KZ_InformationLaw"] },
    t2: { mitre: ["T1190", "T1528"], kz: ["KZ_PersonalData"] },
    t3: {
      mitre: ["T1190", "T1528"],
      kz: ["KZ_Criminal204", "KZ_PersonalData"],
    },
    t4: { mitre: ["T1087"], kz: ["KZ_PersonalData"] },
    t5: { mitre: ["M1023", "M1000"], kz: ["KZ_InformationLaw"] },
    t6: { mitre: [], kz: ["KZ_InformationLaw"] },
    t7: { mitre: ["T1190"], kz: ["KZ_InformationLaw"] },
    t8: { mitre: ["T1190", "T1005"], kz: ["KZ_CriminalProcedure"] },
    t9: { mitre: ["T1087"], kz: ["KZ_InformationLaw"] },
    t10: { mitre: [], kz: ["KZ_CriminalProcedure", "KZ_InformationLaw"] },
  },

  // ── Цифровая форензика: расследование инцидента ─────────────────────
  "lab-scenario-4": {
    t1: { mitre: [], kz: ["KZ_CriminalProcedure"] },
    t2: {
      mitre: ["T1190"],
      kz: ["KZ_CriminalProcedure", "KZ_Criminal204"],
    },
    t3: {
      mitre: ["T1190", "T1528"],
      kz: ["KZ_PersonalData", "KZ_CriminalProcedure"],
    },
    t4: { mitre: ["T1190", "T1528"], kz: ["KZ_Criminal204"] },
    t5: {
      mitre: ["T1110.001", "T1078"],
      kz: ["KZ_Criminal204"],
    },
    t6: { mitre: ["T1110.001"], kz: ["KZ_Criminal204"] },
    t7: {
      mitre: ["T1190", "T1059.004", "T1003.008"],
      kz: ["KZ_Criminal204"],
    },
    t8: { mitre: [], kz: ["KZ_CriminalProcedure"] },
    t9: { mitre: [], kz: ["KZ_CriminalProcedure"] },
    t10: {
      mitre: ["M1023", "M1000"],
      kz: ["KZ_CriminalProcedure", "KZ_InformationLaw"],
    },
  },
};

/** mapping для задачи (undefined → без соответствия). */
export function mappingFor(
  scenarioId: string,
  taskId: string,
): TaskMapping | undefined {
  return TASK_MAPPING[scenarioId]?.[taskId];
}

/** Человекочитаемые названия для чипов (массив строк). */
export function mappingLabels(m: TaskMapping): string[] {
  const out: string[] = [];
  for (const id of m.mitre ?? []) out.push(MITRE_NAMES[id] ?? id);
  for (const id of m.kz ?? []) out.push(KZ_REFS[id] ?? id);
  return out;
}
